import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import { AuthLayout } from "../layouts/AuthLayout.js";
import { AppShell } from "../layouts/AppShell.js";
import { StandaloneLayout } from "../layouts/StandaloneLayout.js";
import {
  RedirectIfAuthenticated,
  RequireAdmin,
  RequireAuth,
  RequireOrg,
  RequireTeamPlan,
  RequireVerified,
} from "../guards/index.js";
import { RouteChange } from "./RouteChange.js";
import { Signup } from "../routes/auth/Signup.js";
import { Login } from "../routes/auth/Login.js";
import { Reset, ResetConfirm } from "../routes/auth/Reset.js";
import { Verify } from "../routes/auth/Verify.js";
import { Onboarding } from "../routes/Onboarding.js";
import { Dashboard } from "../routes/Dashboard.js";
import { Account } from "../routes/Account.js";
import { Settings } from "../routes/Settings.js";
import { Team } from "../routes/Team.js";
import { PolicyEditor } from "../routes/team/PolicyEditor.js";
import { Billing } from "../routes/Billing.js";
import { Subscription } from "../routes/Subscription.js";
import { Checkout } from "../routes/checkout/Checkout.js";
import { CheckoutSuccess } from "../routes/checkout/CheckoutSuccess.js";
import { CheckoutCancelled } from "../routes/checkout/CheckoutCancelled.js";
import { NotFound } from "../routes/NotFound.js";

/**
 * The route tree.
 *
 * Guards are route-level wrappers composed IN THE TREE, never `if` statements inside a
 * screen: a screen that renders and then redirects has already fired its queries and flashed
 * its skeletons.
 *
 * `status = locked` appears NOWHERE in this tree. Every route below still resolves for a
 * locked org and the CONTENT degrades instead — because /billing, /subscription and
 * /checkout are the screens that unlock the account, and a lockout there would leave the
 * customer no way to pay us.
 */
function Root() {
  return (
    <>
      <RouteChange />
      <Outlet />
    </>
  );
}

export const routes = [
  {
    element: <Root />,
    children: [
      // ---- public, archetype A -------------------------------------------------
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/signup",
            element: (
              <RedirectIfAuthenticated signup>
                <Signup />
              </RedirectIfAuthenticated>
            ),
          },
          {
            path: "/login",
            element: (
              <RedirectIfAuthenticated>
                <Login />
              </RedirectIfAuthenticated>
            ),
          },
          { path: "/reset", element: <Reset /> },
          { path: "/reset/confirm", element: <ResetConfirm /> },
          // /verify needs a session but NOT a verified one — it is where you go to get one.
          {
            element: <RequireAuth />,
            children: [{ path: "/verify", element: <Verify /> }],
          },
        ],
      },

      // ---- authenticated -------------------------------------------------------
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireVerified />,
            children: [
              // Onboarding deliberately does NOT require an org: it is where a team signup
              // creates one, and installing the extension is free regardless.
              {
                element: <StandaloneLayout />,
                children: [{ path: "/onboarding", element: <Onboarding /> }],
              },
              {
                element: <RequireOrg />,
                children: [
                  {
                    element: <AppShell />,
                    children: [
                      { path: "/", element: <Dashboard /> },
                      { path: "/account", element: <Account /> },
                      { path: "/settings", element: <Settings /> },
                      // Billing stays OUTSIDE RequireTeamPlan: an individual admin still pays us.
                      {
                        element: <RequireTeamPlan />,
                        children: [
                          { path: "/team", element: <Team /> },
                          {
                            element: <RequireAdmin />,
                            children: [
                              { path: "/team/policy", element: <PolicyEditor /> },
                              // API keys are not a live surface yet. ApiKeys.tsx stays on disk.
                              { path: "/team/api-keys", element: <Navigate to="/team" replace /> },
                            ],
                          },
                        ],
                      },
                      {
                        element: <RequireAdmin />,
                        children: [
                          { path: "/billing", element: <Billing /> },
                          { path: "/subscription", element: <Subscription /> },
                        ],
                      },
                    ],
                  },
                  {
                    element: <StandaloneLayout />,
                    children: [
                      { path: "/checkout/cancelled", element: <CheckoutCancelled /> },
                      {
                        element: <RequireAdmin />,
                        children: [
                          { path: "/checkout", element: <Checkout /> },
                          { path: "/checkout/success", element: <CheckoutSuccess /> },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ---- 404 -----------------------------------------------------------------
      {
        element: <StandaloneLayout />,
        children: [
          { path: "/404", element: <NotFound /> },
          { path: "*", element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
